/**
 * Variance ranked-site table — master-detail.
 *
 * Each site row (worst deviation first) expands to its anomalous bill cycles.
 * Bespoke cells (peer-percentile strip, trend sparkline, provenance chip) have
 * no nzds equivalent, so they're built here on MUI + theme tokens — the same
 * custom-component approach as the E2 detail view. Cerulean is the WATCH-zone
 * accent.
 */
import type React from "react"
import { Fragment, useState } from "react"

import { useFormatCurrency } from "@/formatters/use-format-currency"

import {
  ExpandMoreRounded,
  InsightsRounded,
  ReceiptLongRounded,
  SensorsRounded,
  WarningAmberRounded,
} from "@mui/icons-material"
import {
  Box,
  Chip,
  Collapse,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material"
import { type Theme, useTheme } from "@mui/material/styles"

import { cerulean } from "../../homepage/atoms"
import { BillDrawer } from "./bill-drawer"
import { DisaggregationDrawer } from "./disaggregation-drawer"
import { Sparkline } from "./sparkline"
import type {
  AnomalousCycle,
  VariancePeerPosition,
  VarianceProvenance,
  VarianceSiteRow,
} from "./variance.types"

// ── cells ─────────────────────────────────────────────────────────────────────

const ColHead: React.FC<React.PropsWithChildren> = ({ children }) => (
  <Typography
    color="text.secondary"
    sx={{
      textTransform: "uppercase",
      fontWeight: "bold",
      letterSpacing: "0.06em",
    }}
    variant="dataLabel"
  >
    {children}
  </Typography>
)

/** Percentile within the peer cohort + a marker strip (median tick at 50%). */
const PeerPositionCell: React.FC<{ peer: VariancePeerPosition }> = ({
  peer,
}) => {
  const theme = useTheme()
  const accent = cerulean(theme)
  const ordinal =
    peer.percentile % 10 === 1 && peer.percentile !== 11
      ? "st"
      : peer.percentile % 10 === 2 && peer.percentile !== 12
        ? "nd"
        : peer.percentile % 10 === 3 && peer.percentile !== 13
          ? "rd"
          : "th"

  return (
    <Stack spacing={0.75}>
      <Stack direction="row" spacing={0.75} sx={{ alignItems: "baseline" }}>
        <Typography sx={{ fontWeight: "bold" }} variant="body2">
          {peer.percentile}
          {ordinal} pctile
        </Typography>
        <Typography color="text.secondary" variant="dataLabel">
          of {peer.cohort_size} sites
        </Typography>
      </Stack>
      <Box
        sx={{
          position: "relative",
          width: 104,
          height: 6,
          borderRadius: 9999,
          bgcolor: theme.palette.grey[100],
        }}
      >
        {/* median tick */}
        <Box
          sx={{
            position: "absolute",
            top: -2,
            left: "50%",
            width: "1px",
            height: 10,
            bgcolor: theme.palette.grey[300],
          }}
        />
        {/* site marker */}
        <Box
          sx={{
            position: "absolute",
            top: -2,
            left: `${peer.percentile}%`,
            width: 3,
            height: 10,
            borderRadius: 1,
            bgcolor: accent.main,
          }}
        />
      </Box>
    </Stack>
  )
}

const ProvenanceChip: React.FC<{ provenance: VarianceProvenance }> = ({
  provenance,
}) => {
  const theme = useTheme()
  const accent = cerulean(theme)
  if (provenance === "ami") {
    return (
      <Chip
        icon={<SensorsRounded />}
        label="AMI-backed"
        size="small"
        sx={{
          fontWeight: "bold",
          letterSpacing: "0.04em",
          color: accent.dark,
          bgcolor: accent.surface,
          borderColor: accent.light,
          "& .MuiChip-icon": { color: accent.main },
        }}
        variant="outlined"
      />
    )
  }
  return (
    <Chip
      icon={<ReceiptLongRounded />}
      label="bill-derived"
      size="small"
      sx={{ fontWeight: "bold", letterSpacing: "0.04em" }}
      variant="outlined"
    />
  )
}

/** Humanize a `flagged_by` resource key for the row-level reason line. */
const FLAGGED_BY_LABEL: Record<string, string> = {
  base: "own baseline",
}

const flaggedByLabel = (resource: string): string =>
  FLAGGED_BY_LABEL[resource] ?? resource

/** Humanize an anomalous month's `resource` for display (e.g. table headers). */
const RESOURCE_LABEL: Record<string, string> = {
  electricity: "electricity",
  natural_gas: "natural gas",
  water: "water",
}

const resourceLabel = (resource: string): string =>
  RESOURCE_LABEL[resource] ?? resource

/** Distinct spectrum hue per resource so the month chips read at a glance:
 * electricity = mustard (energy), natural gas = blue (flame), water = cerulean. */
const RESOURCE_SPECTRUM: Record<string, "mustard" | "blue" | "cerulean"> = {
  electricity: "mustard",
  natural_gas: "blue",
  water: "cerulean",
}

const resourceChipSx = (resource: string, theme: Theme) => {
  const hue = theme.palette.spectrum[RESOURCE_SPECTRUM[resource] ?? "mustard"]
  return {
    fontWeight: "bold",
    letterSpacing: "0.04em",
    color: hue[500],
    bgcolor: hue[50],
    borderColor: hue[100],
  } as const
}

/** Short date from an ISO date-only string (e.g. "2026-03-01"). Pinned to a
 * UTC instant (`T00:00:00Z`) before formatting so it never shifts a
 * day/month across runtimes/local timezones. */
const dateLabel = (iso: string, withYear: boolean): string =>
  new Date(`${iso.slice(0, 10)}T00:00:00Z`).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    ...(withYear ? { year: "numeric" as const } : {}),
    timeZone: "UTC",
  })

/** Row label for a bill cycle: its service period, end-dated with the year. */
const servicePeriodLabel = (period: { start: string; end: string }): string =>
  `${dateLabel(period.start, false)} – ${dateLabel(period.end, true)}`

export type CycleAnomalyKind = "over_expected" | "mismatch" | "seasonal"

/**
 * Which anomaly the row leads with. `over_expected` (AMI ran hot vs the
 * weather-normalized baseline) takes priority over a `mismatch` (AMI ≠
 * billed data-reconciliation flag); anything else is a seasonal-basis
 * outlier (gas/water, or an electric cycle carrying neither boolean).
 */
export const cycleAnomalyKind = (cycle: AnomalousCycle): CycleAnomalyKind => {
  if (cycle.over_expected) {
    return "over_expected"
  }
  if (cycle.mismatch) {
    return "mismatch"
  }
  return "seasonal"
}

const CYCLE_ANOMALY_LABEL: Record<CycleAnomalyKind, string> = {
  over_expected: "Over expected",
  mismatch: "Metered ≠ billed",
  seasonal: "Seasonal spike",
}

/** The anomaly type chip per row — specifics (gauge, values, $, mismatch
 * detail) live in the View-details drawer. Every row in `bill_cycles` is
 * already anomalous (the BFF only sends flagged cycles), so there's no
 * "Normal" state here. */
const CycleAnomalyChip: React.FC<{ cycle: AnomalousCycle }> = ({ cycle }) => (
  <Chip
    color="warning"
    label={CYCLE_ANOMALY_LABEL[cycleAnomalyKind(cycle)]}
    size="small"
    sx={{ fontWeight: "bold" }}
    variant="outlined"
  />
)

/** Cycle-total $ then usage — dollars lead when a tariff resolved
 * (`dollars_over`, falling back to `actual_dollars`), usage alone otherwise.
 * Both dollar fields are `number | null | undefined`, so this only trusts an
 * actual number. */
const CycleAmountCell: React.FC<{ cycle: AnomalousCycle }> = ({ cycle }) => {
  const { formatCurrency } = useFormatCurrency({
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })
  const amount = cycle.dollars_over ?? cycle.actual_dollars ?? null
  const usageText = `${cycle.usage.toLocaleString()} ${cycle.usage_unit}`
  return (
    <Typography
      sx={{ fontWeight: "bold", fontVariantNumeric: "tabular-nums" }}
      variant="body2"
    >
      {amount != null ? `${formatCurrency(amount)} · ${usageText}` : usageText}
    </Typography>
  )
}

const FlaggedByLine: React.FC<{ flaggedBy: readonly string[] }> = ({
  flaggedBy,
}) => (
  <Typography color="text.secondary" variant="dataLabel">
    Flagged by {flaggedBy.map(flaggedByLabel).join(", ")}
  </Typography>
)

/** Composite Site EUI + its per-resource breakdown — expanded view only. */
const ResourceEuiSummary: React.FC<{
  siteEui: number
  resources?: VarianceSiteRow["resources"]
}> = ({ siteEui, resources }) => {
  const entries = resources ? Object.entries(resources) : []

  return (
    <Stack
      direction="row"
      spacing={3}
      sx={{ px: 4, pt: 1.5, alignItems: "baseline" }}
    >
      <Stack spacing={0.25}>
        <Typography color="text.secondary" variant="dataLabel">
          Site EUI
        </Typography>
        <Typography sx={{ fontWeight: "bold" }} variant="body2">
          {siteEui}
          <Typography
            color="text.secondary"
            component="span"
            sx={{ fontWeight: "normal" }}
            variant="dataLabel"
          >
            {" "}
            kBtu/sqft/yr
          </Typography>
        </Typography>
      </Stack>
      {entries.map(([resource, r]) => (
        <Stack key={resource} spacing={0.25}>
          <Typography
            color="text.secondary"
            sx={{ textTransform: "capitalize" }}
            variant="dataLabel"
          >
            {resource} EUI
          </Typography>
          <Typography sx={{ fontWeight: "bold" }} variant="body2">
            {r.eui}
            {r.percentile != null ? (
              <Typography
                color="text.secondary"
                component="span"
                sx={{ fontWeight: "normal" }}
                variant="dataLabel"
              >
                {" "}
                · {r.percentile}th pctile
                {r.cohort_size != null ? ` of ${r.cohort_size}` : ""}
              </Typography>
            ) : null}
          </Typography>
        </Stack>
      ))}
    </Stack>
  )
}

// ── sub-table (anomalous bill cycles) ──────────────────────────────────────────

const AnomalousCyclesTable: React.FC<{
  cycles: readonly AnomalousCycle[]
  onViewCycle: (cycle: AnomalousCycle) => void
  onViewDisagg: (cycle: AnomalousCycle) => void
}> = ({ cycles, onViewCycle, onViewDisagg }) => {
  const theme = useTheme()
  const label = `${cycles.length} anomalous ${
    cycles.length === 1 ? "cycle" : "cycles"
  }`

  return (
    <Box sx={{ pl: 4, pr: 1, py: 1.5 }}>
      <Typography
        color="text.secondary"
        sx={{
          display: "block",
          textTransform: "uppercase",
          fontWeight: "bold",
          letterSpacing: "0.1em",
          mb: 1,
        }}
        variant="dataLabel"
      >
        {label}
      </Typography>
      <Table
        size="small"
        sx={{
          bgcolor: theme.palette.common.white,
          borderRadius: theme.typography.pxToRem(6),
          "& td, & th": { borderColor: theme.palette.grey[100] },
        }}
      >
        <TableHead>
          <TableRow>
            <TableCell>
              <ColHead>Service period</ColHead>
            </TableCell>
            <TableCell>
              <ColHead>Resource</ColHead>
            </TableCell>
            <TableCell>
              <ColHead>$ · usage</ColHead>
            </TableCell>
            <TableCell>
              <ColHead>Anomaly</ColHead>
            </TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {cycles.map((cycle) => {
            const resource = resourceLabel(cycle.resource)

            return (
              <TableRow
                key={`${cycle.resource}-${cycle.service_period.start}-${cycle.service_period.end}`}
                sx={{
                  "& > td:first-of-type": {
                    borderLeft: `3px solid ${theme.palette.warning.main}`,
                  },
                }}
              >
                <TableCell>
                  <Typography
                    sx={{ fontVariantNumeric: "tabular-nums" }}
                    variant="body2"
                  >
                    {`${servicePeriodLabel(cycle.service_period)} · ${cycle.days}d`}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={resource}
                    size="small"
                    sx={resourceChipSx(cycle.resource, theme)}
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  <CycleAmountCell cycle={cycle} />
                </TableCell>
                <TableCell>
                  <CycleAnomalyChip cycle={cycle} />
                </TableCell>
                <TableCell align="right">
                  <Stack
                    direction="row"
                    spacing={0.5}
                    sx={{ justifyContent: "flex-end" }}
                  >
                    <Tooltip title="View details">
                      <IconButton
                        aria-label="View details"
                        onClick={() => {
                          onViewCycle(cycle)
                        }}
                        size="small"
                      >
                        <ReceiptLongRounded fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {/* PRISM disaggregates the ELECTRIC meter only — the
                        end-use drawer is meaningless for a gas/water anomaly, so
                        only offer it on electricity rows. */}
                    {cycle.resource === "electricity" ? (
                      <Tooltip title="Electric end uses">
                        <IconButton
                          aria-label="Electric end uses"
                          onClick={() => {
                            onViewDisagg(cycle)
                          }}
                          size="small"
                        >
                          <InsightsRounded fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : null}
                  </Stack>
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </Box>
  )
}

// ── master row ────────────────────────────────────────────────────────────────

const VarianceRow: React.FC<{
  site: VarianceSiteRow
  onViewCycle: (cycle: AnomalousCycle) => void
  onViewDisagg: (siteId: string, cycle: AnomalousCycle) => void
}> = ({ site, onViewCycle, onViewDisagg }) => {
  const theme = useTheme()
  const accent = cerulean(theme)
  const [open, setOpen] = useState(false)
  const { formatCurrency } = useFormatCurrency({
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })

  return (
    <Fragment>
      <TableRow
        hover
        onClick={() => {
          setOpen((v) => !v)
        }}
        sx={{
          cursor: "pointer",
          "& > td": { borderBottom: "none" },
          ...(open ? { bgcolor: accent.surface } : {}),
        }}
      >
        <TableCell sx={{ width: 34 }}>
          <IconButton aria-label={open ? "Collapse" : "Expand"} size="small">
            <ExpandMoreRounded
              sx={{
                transition: "transform .18s",
                transform: open ? "rotate(180deg)" : "none",
                color: open ? accent.dark : "text.disabled",
              }}
            />
          </IconButton>
        </TableCell>
        <TableCell>
          <Stack spacing={0.25}>
            <Typography sx={{ fontWeight: "bold" }} variant="body1">
              {site.site_label}
            </Typography>
            <Typography color="text.secondary" variant="dataLabel">
              {site.site_meta}
            </Typography>
            {site.flagged_by && site.flagged_by.length > 0 ? (
              <FlaggedByLine flaggedBy={site.flagged_by} />
            ) : null}
            {site.data_incomplete ? (
              <Tooltip title="Gas usage is missing for this site over the window, so its total-energy peer ranking understates true intensity. Treat the position as a lower bound.">
                <Stack
                  direction="row"
                  spacing={0.5}
                  sx={{ alignItems: "center", mt: 0.25 }}
                >
                  <WarningAmberRounded
                    sx={{ fontSize: 13, color: "warning.main" }}
                  />
                  <Typography
                    color="warning.main"
                    sx={{ fontWeight: "bold" }}
                    variant="dataLabel"
                  >
                    Gas data incomplete
                  </Typography>
                </Stack>
              </Tooltip>
            ) : null}
          </Stack>
        </TableCell>
        <TableCell>
          <PeerPositionCell peer={site.peer} />
        </TableCell>
        <TableCell>
          <Stack spacing={0.25}>
            {site.dollars_above_expected > 0 ? (
              <>
                <Typography
                  sx={{
                    fontWeight: "bold",
                    fontVariantNumeric: "tabular-nums",
                  }}
                  variant="body2"
                >
                  {formatCurrency(site.dollars_above_expected)}
                </Typography>
                <Typography color="text.secondary" variant="dataLabel">
                  above expected
                </Typography>
              </>
            ) : (
              // $0 here means no tariff resolved for the site's electric
              // meter (unmapped / rate DB unreachable) — a flagged site always
              // has positive excess. Showing "$0 above expected" reads as
              // "no overage cost," which is wrong, so render a neutral dash.
              <Tooltip title="No electric rate schedule is mapped for this site, so the dollar impact of the overage can't be priced.">
                <span>
                  <Typography
                    color="text.secondary"
                    sx={{ fontWeight: "bold" }}
                    variant="body2"
                  >
                    —
                  </Typography>
                  <Typography color="text.secondary" variant="dataLabel">
                    cost unavailable
                  </Typography>
                </span>
              </Tooltip>
            )}
          </Stack>
        </TableCell>
        <TableCell>
          <Sparkline
            actual={site.trend.actual}
            expected={site.trend.expected}
            stroke={accent.main}
          />
        </TableCell>
        <TableCell>
          <ProvenanceChip provenance={site.provenance} />
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell
          colSpan={6}
          sx={{ py: 0, borderBottom: `1px solid ${theme.palette.grey[100]}` }}
        >
          <Collapse in={open} mountOnEnter timeout="auto" unmountOnExit>
            <Box sx={{ bgcolor: theme.palette.grey[25] }}>
              <ResourceEuiSummary
                resources={site.resources}
                siteEui={site.site_eui}
              />
              <AnomalousCyclesTable
                cycles={site.bill_cycles}
                onViewCycle={onViewCycle}
                onViewDisagg={(cycle) => {
                  onViewDisagg(site.site_id, cycle)
                }}
              />
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </Fragment>
  )
}

// ── table ─────────────────────────────────────────────────────────────────────

export const VarianceTable: React.FC<{
  sites: readonly VarianceSiteRow[]
}> = ({ sites }) => {
  const [active, setActive] = useState<AnomalousCycle | null>(null)
  const [activeDisagg, setActiveDisagg] = useState<{
    siteId: string
    cycle: AnomalousCycle
  } | null>(null)

  return (
    <>
      <Table aria-label="Ranked site variance">
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: 34 }} />
            <TableCell>
              <ColHead>Site</ColHead>
            </TableCell>
            <TableCell>
              <ColHead>Peer position</ColHead>
            </TableCell>
            <TableCell>
              <ColHead>$ context</ColHead>
            </TableCell>
            <TableCell>
              <ColHead>Trend</ColHead>
            </TableCell>
            <TableCell>
              <ColHead>Provenance</ColHead>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sites.map((site) => (
            <VarianceRow
              key={site.site_id}
              onViewCycle={setActive}
              onViewDisagg={(siteId, cycle) => {
                setActiveDisagg({ siteId, cycle })
              }}
              site={site}
            />
          ))}
        </TableBody>
      </Table>
      <BillDrawer
        month={active}
        onClose={() => {
          setActive(null)
        }}
        open={active != null}
      />
      <DisaggregationDrawer
        cycle={activeDisagg?.cycle ?? null}
        onClose={() => {
          setActiveDisagg(null)
        }}
        open={activeDisagg != null}
        siteId={activeDisagg?.siteId ?? null}
      />
    </>
  )
}
